// BuilderEditor — full-screen editor for a single built app.
//
// Three-pane layout:
//   - Chat (left)         AI assistant conversation, streams tool calls + text
//   - File explorer (mid) tree of project files; clicking opens an editor
//   - Preview (right)     live AppRuntime rendering the project files
//
// The chat panel and preview update together: when the AI's tool calls mutate
// files, the editor refetches the file map and the preview re-renders.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { v4 as uuidv4 } from "uuid";

import LoadingButton from "../../components/LoadingButton";
import LucideIcon from "../../components/LucideIcon";
import ChatInput from "../../components/ChatInput";
import { useDictation } from "../../hooks/useDictation";
import { APP_TYPE_EXTERNAL } from "../../constants";
import { hostname } from "../../utils";
import type { BuilderApiClient } from "./api";
import type {
  BuilderApp,
  BuilderChatAttachment,
  BuilderChatMessage,
  BuilderFile,
  BuilderFileEntry,
  BuilderThreadSummary,
  BuilderToolEvent,
  FlexSchema,
} from "./types";
import AppRuntime, { type RuntimeEvent } from "./runtime/AppRuntime";
import CodeEditor from "./CodeEditor";
import RuntimeConsolePanel, {
  type ConsoleEntry,
} from "./RuntimeConsolePanel";
import { parseSseStream } from "./sse";
import GitFilesMenu from "./dialogs/GitFilesMenu";
import ThemePanel from "./dialogs/ThemePanel";
import PublishMenu from "./dialogs/PublishMenu";

interface Props {
  api: BuilderApiClient;
  appId: string;
  companyId: string;
  getToken: () => Promise<string>;
  onClose: () => void;
  openDialog: (title: string, content: string, buttonText: string, onClick: () => void) => void;
  currentUid?: string;
  menuAppIds: string[];
  onMenuAppIdsChange: (ids: string[]) => void;
  // Seeded from the apps-list empty-state hero. When present, auto-sends as
  // the first message once the editor loads an empty thread.
  initialPrompt?: string;
}

interface ChatMsg extends BuilderChatMessage {
  isStreaming?: boolean;
}

interface TodoItem {
  content: string;
  status: "pending" | "in_progress" | "completed";
  activeForm?: string;
}

// Walk the chat history backward to find the most recent todo_write tool call,
// but only within the *current* assistant turn — i.e. stop at the latest user
// message. Otherwise a partially-completed todo list from an earlier task
// keeps re-rendering for the rest of the conversation. The agent always
// passes the complete todo list each time it calls the tool, so the latest
// call in the current turn is the canonical state.
function extractLatestTodos(messages: ChatMsg[]): TodoItem[] {
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i];
    if (msg.role === "user") return [];
    const calls = msg.toolCalls || [];
    for (let j = calls.length - 1; j >= 0; j--) {
      const c = calls[j];
      if (c.name === "todo_write") {
        const todos = (c.args?.todos || []) as any[];
        return todos
          .filter((t) => t && typeof t === "object")
          .map((t) => ({
            content: String(t.content || ""),
            status: (t.status === "in_progress" || t.status === "completed"
              ? t.status
              : "pending") as TodoItem["status"],
            activeForm: t.activeForm ? String(t.activeForm) : undefined,
          }));
      }
    }
  }
  return [];
}

const TOOL_DISPLAY: Record<string, string> = {
  list_files: "Listing files",
  read_file: "Reading file",
  write_file: "Writing file",
  edit_file: "Editing file",
  delete_file: "Deleting file",
  validate: "Validating",
  list_tables: "Listing tables",
  create_table: "Creating table",
  add_column: "Adding column",
  delete_column: "Deleting column",
  delete_table: "Deleting table",
};

const SUGGESTIONS = [
  "Build a leads dashboard with a search box and a table",
  "Add a form that captures name, email, and a message",
  "Create a tasks tracker with status tabs",
];

const BuilderEditor: React.FC<Props> = ({
  api,
  appId,
  companyId,
  getToken,
  onClose,
  openDialog,
  currentUid,
  menuAppIds,
  onMenuAppIdsChange,
  initialPrompt,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [leftDrawerOpen, setLeftDrawerOpen] = useState(false);

  const [app, setApp] = useState<BuilderApp | null>(null);
  const [files, setFiles] = useState<Record<string, BuilderFile>>({});
  const [fileEntries, setFileEntries] = useState<BuilderFileEntry[]>([]);
  const [flexSchemas, setFlexSchemas] = useState<FlexSchema[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [chat, setChat] = useState<ChatMsg[]>([]);
  const [prompt, setPrompt] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const [attachments, setAttachments] = useState<BuilderChatAttachment[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const chatScrollRef = useRef<HTMLDivElement | null>(null);

  const {
    isRecording: isDictating,
    isLoading: isDictationLoading,
    startDictation,
    stopDictation,
  } = useDictation({
    onTranscript: (text, isFinal) => {
      if (isFinal && text.trim()) {
        setPrompt((prev) => {
          const trimmedPrev = prev.trim();
          if (trimmedPrev) {
            return trimmedPrev + " " + text.trim();
          }
          return text.trim();
        });
      }
    },
  });

  const [threads, setThreads] = useState<BuilderThreadSummary[]>([]);
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  const [renamingThread, setRenamingThread] = useState<BuilderThreadSummary | null>(null);
  const [renameDraft, setRenameDraft] = useState("");

  const [activeFile, setActiveFile] = useState<string | null>(null);
  const [editorContent, setEditorContent] = useState("");
  const [editorDirty, setEditorDirty] = useState(false);
  const [editorError, setEditorError] = useState<string | null>(null);
  const [savingFile, setSavingFile] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const [rightTab, setRightTab] = useState<"preview" | "code">("preview");
  const [previewKey, setPreviewKey] = useState(0);
  const [previewViewport, setPreviewViewport] = useState<
    "desktop" | "tablet" | "mobile"
  >("desktop");
  // Anchor for the Theme popover. Only rendered for external apps — internal
  // apps inherit the platform's MUI theme and have no notion of CSS-vars/Tailwind.
  const [themeAnchor, setThemeAnchor] = useState<HTMLElement | null>(null);

  // Runtime console state. Errors and warnings forwarded by AppRuntime are
  // deduped by `fingerprint` (kind + first line of message) — repeated
  // identical errors increment a counter instead of stacking. The first
  // time we see a new error fingerprint we open a confirm dialog asking the
  // user whether to forward it to the agent. We never prompt twice for the
  // same fingerprint so a noisy error can't trap the user in a dialog loop.
  const [consoleOpen, setConsoleOpen] = useState(false);
  const [consoleEntries, setConsoleEntries] = useState<ConsoleEntry[]>([]);
  // Fingerprints we've already prompted about in this session. Survives clears.
  const promptedFingerprintsRef = useRef<Set<string>>(new Set());

  const [leftTab, setLeftTab] = useState<"chat" | "files" | "tables">("chat");

  const [createFileOpen, setCreateFileOpen] = useState(false);
  const [createFilePath, setCreateFilePath] = useState("");

  const loadFiles = useCallback(async () => {
    const entries = await api.listFiles(appId);
    setFileEntries(entries);
    // Read each file's content; the file map is small (apps are small)
    const next: Record<string, BuilderFile> = {};
    await Promise.all(
      entries.map(async (e) => {
        const f = await api.readFile(appId, e.path);
        next[e.path] = { content: f.content, type: f.type };
      }),
    );
    setFiles(next);
  }, [api, appId]);

  const loadThread = useCallback(
    async (threadId: string) => {
      const messages = await api.getThreadMessages(appId, threadId);
      setChat(messages as ChatMsg[]);
      setActiveThreadId(threadId);
    },
    [api, appId],
  );

  const loadApp = useCallback(async () => {
    setLoading(true);
    try {
      const [a, schemas, threadsView] = await Promise.all([
        api.getApp(appId),
        api.listFlexSchemas(appId).catch(() => []),
        api.listThreads(appId),
      ]);
      setApp(a);
      setFlexSchemas(schemas);
      setThreads(threadsView.threads || []);
      setActiveThreadId(threadsView.activeThreadId);
      // Load the active thread's messages
      if (threadsView.activeThreadId) {
        const messages = await api.getThreadMessages(
          appId,
          threadsView.activeThreadId,
        );
        setChat(messages as ChatMsg[]);
      } else {
        setChat([]);
      }
      await loadFiles();
      setError(null);
    } catch (e: any) {
      setError(String(e?.response?.data?.error || e?.message || e));
    } finally {
      setLoading(false);
    }
  }, [api, appId, loadFiles]);

  const switchThread = useCallback(
    async (threadId: string) => {
      if (threadId === activeThreadId) return;
      try {
        await api.setActiveThread(appId, threadId);
        await loadThread(threadId);
        // Refresh thread list summaries (counts, updatedAt)
        const view = await api.listThreads(appId);
        setThreads(view.threads || []);
      } catch (e: any) {
        setError(String(e?.response?.data?.error || e?.message || e));
      }
    },
    [api, appId, activeThreadId, loadThread],
  );

  const newThread = useCallback(async () => {
    try {
      const t = await api.createThread(appId);
      const view = await api.listThreads(appId);
      setThreads(view.threads || []);
      setActiveThreadId(t.id);
      setChat([]);
      setLeftTab("chat");
    } catch (e: any) {
      setError(String(e?.response?.data?.error || e?.message || e));
    }
  }, [api, appId]);

  const removeThread = useCallback(
    (threadId: string) => {
      const target = threads.find((t) => t.id === threadId);
      openDialog(
        target ? `Delete thread "${target.title || "Untitled"}"?` : "Delete thread?",
        "This will permanently delete the thread and all of its messages.",
        "Delete",
        async () => {
          try {
            const view = await api.deleteThread(appId, threadId);
            setThreads(view.threads || []);
            setActiveThreadId(view.activeThreadId);
            if (view.activeThreadId) {
              const messages = await api.getThreadMessages(appId, view.activeThreadId);
              setChat(messages as ChatMsg[]);
            } else {
              setChat([]);
            }
          } catch (e: any) {
            setError(String(e?.response?.data?.error || e?.message || e));
          }
        },
      );
    },
    [api, appId, threads, openDialog],
  );

  const submitRename = useCallback(async () => {
    if (!renamingThread) return;
    const title = renameDraft.trim();
    if (!title) {
      setRenamingThread(null);
      return;
    }
    try {
      await api.renameThread(appId, renamingThread.id, title);
      const view = await api.listThreads(appId);
      setThreads(view.threads || []);
    } catch (e: any) {
      setError(String(e?.response?.data?.error || e?.message || e));
    } finally {
      setRenamingThread(null);
    }
  }, [api, appId, renamingThread, renameDraft]);

  useEffect(() => {
    loadApp();
  }, [loadApp]);

  // Auto-send a seed prompt from the apps-list empty-state hero. Fires once
  // after the editor finishes loading an empty thread for this app.
  const seedFiredRef = useRef(false);
  useEffect(() => {
    if (seedFiredRef.current) return;
    if (loading || chatLoading) return;
    if (!initialPrompt || !initialPrompt.trim()) return;
    if (chat.length > 0) return;
    seedFiredRef.current = true;
    sendMessage(initialPrompt);
    // sendMessage isn't memoized; we intentionally only re-run when the gate
    // conditions change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, chatLoading, initialPrompt, chat.length]);

  // Keep the file editor in sync with files map when external changes happen
  useEffect(() => {
    if (!activeFile) return;
    const f = files[activeFile];
    if (!f) {
      setActiveFile(null);
      return;
    }
    if (!editorDirty) {
      setEditorContent(f.content);
    }
  }, [activeFile, files, editorDirty]);

  const switchToFile = (path: string) => {
    setActiveFile(path);
    setEditorContent(files[path]?.content || "");
    setEditorDirty(false);
    setEditorError(null);
  };

  const onPickFile = (path: string) => {
    if (editorDirty && activeFile && activeFile !== path) {
      openDialog(
        `Discard unsaved changes to ${activeFile}?`,
        "Your edits to this file will be lost.",
        "Discard",
        () => switchToFile(path),
      );
      return;
    }
    switchToFile(path);
  };

  const saveFile = async () => {
    if (!activeFile) return;
    // Only enforce JSON syntax for .json files — TS/TSX/CSS are validated by
    // the in-browser Babel compiler when the preview re-runs.
    if (activeFile.endsWith(".json")) {
      try {
        JSON.parse(editorContent);
      } catch (e: any) {
        setEditorError(`Invalid JSON: ${e.message || e}`);
        return;
      }
    }
    setSavingFile(true);
    setEditorError(null);
    try {
      await api.writeFile(appId, activeFile, editorContent);
      setFiles((prev) => ({
        ...prev,
        [activeFile]: { content: editorContent, type: prev[activeFile]?.type },
      }));
      setEditorDirty(false);
      setPreviewKey((k) => k + 1);
    } catch (e: any) {
      setEditorError(String(e?.response?.data?.error || e?.message || e));
    } finally {
      setSavingFile(false);
    }
  };

  const deleteFile = async (path: string) => {
    try {
      await api.deleteFile(appId, path);
      setFiles((prev) => {
        const next = { ...prev };
        delete next[path];
        return next;
      });
      setFileEntries((prev) => prev.filter((e) => e.path !== path));
      if (activeFile === path) {
        setActiveFile(null);
        setEditorContent("");
        setEditorDirty(false);
      }
      setPreviewKey((k) => k + 1);
    } catch (e: any) {
      setError(String(e?.response?.data?.error || e?.message || e));
    } finally {
      setConfirmDelete(null);
    }
  };

  const onCreateFile = async () => {
    let path = createFilePath.trim();
    if (!path) return;
    if (!path.startsWith("/")) path = "/" + path;
    if (!/\.(tsx|ts|jsx|js|json|css)$/.test(path)) path = path + ".tsx";
    const isJson = path.endsWith(".json");
    const isCss = path.endsWith(".css");
    const componentName = path.split("/").pop()!.replace(/\.(tsx|ts|jsx|js)$/, "");
    let seed: string;
    if (isJson) {
      seed = "{}";
    } else if (isCss) {
      seed = "/* styles */\n";
    } else if (app?.appType === APP_TYPE_EXTERNAL) {
      seed =
        `import React from "react";\n\n` +
        `export default function ${componentName || "Component"}() {\n` +
        `  return (\n` +
        `    <div className="p-4">\n` +
        `      <p>${componentName}</p>\n` +
        `    </div>\n` +
        `  );\n` +
        `}\n`;
    } else {
      seed =
        `import React from "react";\n` +
        `import { Box, Typography } from "@mui/material";\n\n` +
        `export default function ${componentName || "Component"}() {\n` +
        `  return (\n` +
        `    <Box sx={{ p: 2 }}>\n` +
        `      <Typography>${componentName}</Typography>\n` +
        `    </Box>\n` +
        `  );\n` +
        `}\n`;
    }
    try {
      await api.writeFile(appId, path, seed);
      await loadFiles();
      setActiveFile(path);
      setEditorContent(seed);
      setEditorDirty(false);
      setCreateFileOpen(false);
      setCreateFilePath("");
      setPreviewKey((k) => k + 1);
    } catch (e: any) {
      setError(String(e?.response?.data?.error || e?.message || e));
    }
  };

  const handleAttachmentUpload = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (!files || files.length === 0) return;
      setUploadingFiles(true);
      try {
        for (const file of Array.from(files)) {
          if (!file.type.startsWith("image/")) continue;
          const uploaded = await api.uploadImage(appId, file);
          setAttachments((prev) => [...prev, uploaded]);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Failed to upload image:", err);
      } finally {
        setUploadingFiles(false);
        event.target.value = "";
      }
    },
    [api, appId],
  );

  const handleRemoveAttachment = useCallback((index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const sendMessage = async (overrideText?: string) => {
    const text = (overrideText ?? prompt).trim();
    const currentAttachments = [...attachments];
    if ((!text && currentAttachments.length === 0) || chatLoading) return;

    const userMsg: ChatMsg = {
      id: uuidv4(),
      role: "user",
      content: text,
      timestamp: Date.now(),
    };
    const assistantId = uuidv4();
    const assistantMsg: ChatMsg = {
      id: assistantId,
      role: "assistant",
      content: "",
      timestamp: Date.now(),
      isStreaming: true,
      toolCalls: [],
    };

    setChat((prev) => [...prev, userMsg, assistantMsg]);
    setPrompt("");
    setAttachments([]);
    setChatLoading(true);

    const ctrl = new AbortController();
    abortRef.current = ctrl;

    try {
      // Thread history is loaded server-side from the DB; we only send the
      // new user message + the threadId so the server knows where to write it.
      const response = await api.chat(
        appId,
        {
          message: userMsg.content,
          threadId: activeThreadId || undefined,
          clientMessageId: userMsg.id,
          attachments: currentAttachments.length > 0 ? currentAttachments : undefined,
        },
        ctrl.signal,
      );
      if (!response.ok) {
        const text = await response.text().catch(() => "");
        throw new Error(`HTTP ${response.status}: ${text || response.statusText}`);
      }

      let textBuffer = "";
      let pendingToolName: string | null = null;
      let pendingToolArgs: Record<string, any> | null = null;
      let toolCalls: BuilderToolEvent[] = [];
      let streamDone = false;

      const refreshFilesAndPreview = async () => {
        try {
          await loadFiles();
        } catch {
          // best-effort; ignore
        }
        setPreviewKey((k) => k + 1);
      };

      const refreshTablesAndPreview = async () => {
        try {
          const schemas = await api.listFlexSchemas(appId);
          setFlexSchemas(schemas);
        } catch {
          // best-effort; ignore
        }
        setPreviewKey((k) => k + 1);
      };

      for await (const ev of parseSseStream<any>(response)) {
        if (ctrl.signal.aborted) break;
        switch (ev.event) {
          case "text":
            textBuffer += ev.data?.delta || "";
            updateAssistant(assistantId, { content: textBuffer, toolCalls: [...toolCalls] });
            break;
          case "tool_call":
            pendingToolName = ev.data?.name;
            pendingToolArgs = ev.data?.args;
            toolCalls = [
              ...toolCalls,
              { name: pendingToolName!, args: pendingToolArgs || {} },
            ];
            updateAssistant(assistantId, { content: textBuffer, toolCalls: [...toolCalls] });
            break;
          case "tool_result": {
            const last = toolCalls[toolCalls.length - 1];
            if (last && last.name === ev.data?.name) {
              last.result = ev.data?.result;
              last.ok = !!ev.data?.ok;
            }
            updateAssistant(assistantId, { content: textBuffer, toolCalls: [...toolCalls] });
            break;
          }
          case "files_changed":
            // Refresh preview immediately so the user sees changes as they happen.
            void refreshFilesAndPreview();
            break;
          case "tables_changed":
            void refreshTablesAndPreview();
            break;
          case "done":
            streamDone = true;
            break;
          case "error":
            textBuffer += `\n\n_Error: ${ev.data?.message || "unknown"}_`;
            updateAssistant(assistantId, { content: textBuffer });
            break;
          default:
            break;
        }
        if (streamDone) {
          // Tear down the HTTP connection so the chat doesn't sit waiting for
          // the server to close the stream after the `done` event.
          ctrl.abort();
          break;
        }
      }

      // Mark complete in the local optimistic state. The DB-refresh happens
      // in `finally` below so it runs on both happy and error paths.
      setChat((prev) =>
        prev.map((m) => (m.id === assistantId ? { ...m, isStreaming: false } : m)),
      );
    } catch (e: any) {
      // An abort after `done` is the normal path for tearing down the stream
      // — don't surface it as an error to the user.
      if (e?.name === "AbortError" || ctrl.signal.aborted) {
        setChat((prev) =>
          prev.map((m) => (m.id === assistantId ? { ...m, isStreaming: false } : m)),
        );
      } else {
        const message = e?.response?.data?.error || e?.message || String(e);
        setChat((prev) =>
          prev.map((m) =>
            m.id === assistantId
              ? { ...m, content: `Sorry, I hit an error: ${message}`, isStreaming: false }
              : m,
          ),
        );
      }
    } finally {
      setChatLoading(false);
      abortRef.current = null;
      // Always re-sync from the DB at the end — the server persists the assistant
      // message regardless of how the SSE stream ended (network drop, proxy
      // timeout, model returning text + tool_calls in the same response, etc.).
      // Without this, the on-screen message can stay empty even though the DB
      // has the full summary.
      if (activeThreadId) {
        try {
          const messages = await api.getThreadMessages(appId, activeThreadId);
          setChat(messages as ChatMsg[]);
          const view = await api.listThreads(appId);
          setThreads(view.threads || []);
        } catch {
          // best-effort; ignore
        }
      }
    }
  };

  const updateAssistant = (id: string, patch: Partial<ChatMsg>) => {
    setChat((prev) => prev.map((m) => (m.id === id ? { ...m, ...patch } : m)));
  };

  const stopChat = () => {
    abortRef.current?.abort();
  };

  const composeErrorPrompt = (entry: ConsoleEntry): string => {
    const header =
      entry.kind === "warn"
        ? "While previewing the app I'm seeing this warning"
        : "While previewing the app I'm hitting this error";
    const where =
      entry.source && entry.line
        ? `\nLocation: ${entry.source}:${entry.line}${entry.col ? ":" + entry.col : ""}`
        : "";
    const stack = entry.stack ? `\n\nStack trace:\n${entry.stack}` : "";
    return `${header}:\n\n${entry.message}${where}${stack}\n\nPlease investigate and fix it.`;
  };

  const sendErrorToChat = (entry: ConsoleEntry) => {
    const text = composeErrorPrompt(entry);
    setConsoleEntries((prev) =>
      prev.map((e) => (e.id === entry.id ? { ...e, autoSent: true } : e)),
    );
    promptedFingerprintsRef.current.add(entry.fingerprint);
    sendMessage(text);
  };

  const onRuntimeEvent = useCallback(
    (event: RuntimeEvent) => {
      // Filter out React's noisy dev warnings about resource hints and the
      // "Download the React DevTools" message — they're not actionable.
      const msg = (event.message || "").toLowerCase();
      if (
        msg.includes("download the react devtools") ||
        msg.includes("react-dom.development.js")
      ) {
        return;
      }
      const firstLine = (event.message || "").split("\n")[0].slice(0, 200);
      const fingerprint = `${event.kind}:${firstLine}`;
      const newEntryRef: { current: ConsoleEntry | null } = { current: null };
      setConsoleEntries((prev) => {
        const idx = prev.findIndex((e) => e.fingerprint === fingerprint);
        if (idx >= 0) {
          const next = prev.slice();
          next[idx] = {
            ...next[idx],
            count: next[idx].count + 1,
            ts: event.ts,
          };
          return next;
        }
        const id = `${event.ts}-${Math.random().toString(36).slice(2, 8)}`;
        const entry: ConsoleEntry = {
          ...event,
          id,
          fingerprint,
          count: 1,
        };
        newEntryRef.current = entry;
        return [...prev, entry];
      });
      const newEntry = newEntryRef.current;
      // First time we've seen this error fingerprint? Ask the user whether
      // to hand it to the agent. Never prompt twice for the same fingerprint
      // — even if the user declined — so a noisy error can't loop the dialog.
      // Warnings are surfaced silently.
      const isError =
        event.kind === "uncaught" ||
        event.kind === "rejection" ||
        event.kind === "error" ||
        event.kind === "compile";
      if (
        isError &&
        newEntry &&
        !promptedFingerprintsRef.current.has(fingerprint) &&
        !chatLoading
      ) {
        promptedFingerprintsRef.current.add(fingerprint);
        const entry = newEntry;
        const preview = (entry.message || "").split("\n")[0].slice(0, 240);
        openDialog(
          "Send this error to the AI?",
          `The preview just hit an error: "${preview}". Want to ask the agent to fix it?`,
          "Send to AI",
          () => sendErrorToChat(entry),
        );
      }
    },
    // sendMessage / openDialog read via closure on each call. The handler
    // identity stays stable across most renders so the iframe message
    // listener isn't churned.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [chatLoading, openDialog],
  );

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (chatScrollRef.current) {
      chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
    }
  }, [chat]);

  // Build a real tree of folders/files.
  const fileTree = useMemo(() => buildFileTree(fileEntries), [fileEntries]);

  // Latest agent-managed todo list (extracted from todo_write tool calls in chat)
  const todos = useMemo(() => extractLatestTodos(chat), [chat]);

  if (loading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }
  if (error || !app) {
    return (
      <Box p={3}>
        <Button onClick={onClose} startIcon={<LucideIcon name="ArrowLeft" size={20} />} sx={{ mb: 2 }}>
          Back
        </Button>
        <Alert severity="error">{error || "App not found"}</Alert>
      </Box>
    );
  }

  const leftRailContent = (
    <>
      <Tabs
        value={leftTab}
        onChange={(_, v) => {
          setLeftTab(v);
          if (v === "files" && activeFile) setRightTab("code");
        }}
        variant="fullWidth"
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          minHeight: 44,
          "& .MuiTab-root": { minHeight: 44, fontSize: 13, textTransform: "none" },
        }}
      >
        <Tab
          value="chat"
          label="Chat"
          icon={<LucideIcon name="MessageCircle" size={16} />}
          iconPosition="start"
        />
        <Tab
          value="files"
          label={`Files${fileEntries.length ? ` (${fileEntries.length})` : ""}`}
          icon={<LucideIcon name="Folder" size={16} />}
          iconPosition="start"
        />
        <Tab
          value="tables"
          label={`Tables${flexSchemas.length ? ` (${flexSchemas.length})` : ""}`}
          icon={<LucideIcon name="Database" size={16} />}
          iconPosition="start"
        />
      </Tabs>

      {leftTab === "chat" && (
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <ThreadSwitcher
            threads={threads}
            activeThreadId={activeThreadId}
            onPick={switchThread}
            onNew={newThread}
            onRename={(t) => {
              setRenamingThread(t);
              setRenameDraft(t.title);
            }}
            onDelete={removeThread}
          />
          <Box ref={chatScrollRef} sx={{ flex: 1, overflow: "auto", p: 2 }}>
            {chat.length === 0 ? (
              <Box textAlign="center" py={4}>
                <Typography color="text.secondary" gutterBottom>
                  Describe what you'd like to build
                </Typography>
                {fileEntries.length === 0 && (
                  <Stack spacing={1} sx={{ mt: 2 }}>
                    {SUGGESTIONS.map((s) => (
                      <Chip
                        key={s}
                        label={s}
                        onClick={() => setPrompt(s)}
                        sx={{ cursor: "pointer" }}
                      />
                    ))}
                  </Stack>
                )}
              </Box>
            ) : (
              chat.map((m) => <ChatMessageView key={m.id} message={m} />)
            )}
            {chatLoading && (
              <Box display="flex" alignItems="center" gap={1} mt={1}>
                <CircularProgress size={14} />
                <Typography variant="body2" color="text.secondary">
                  Thinking…
                </Typography>
              </Box>
            )}
          </Box>
          <TodoListPanel todos={todos} streaming={chatLoading} />
          <Box sx={{ p: 2, borderTop: 1, borderColor: "divider" }}>
            <ChatInput
              value={prompt}
              onChange={setPrompt}
              onSend={() => sendMessage()}
              onStop={stopChat}
              placeholder="Ask the AI to build or change something…"
              sending={chatLoading}
              disabled={chatLoading}
              rows={4}
              inputId="builder-editor-input"
              showAttachments
              attachments={attachments.map((a) => ({
                id: a.id,
                filename: a.filename,
                type: a.type,
                previewUrl: a.publicUrl || a.url,
              }))}
              onAttachmentUpload={handleAttachmentUpload}
              onRemoveAttachment={handleRemoveAttachment}
              uploadingFiles={uploadingFiles}
              acceptedFileTypes="image/*"
              showDictation
              isDictating={isDictating}
              isDictationLoading={isDictationLoading}
              onStartDictation={startDictation}
              onStopDictation={stopDictation}
            />
          </Box>
        </Box>
      )}

      {leftTab === "files" && (
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <Box
            sx={{
              p: 1,
              pl: 2,
              display: "flex",
              alignItems: "center",
              gap: 1,
              borderBottom: 1,
              borderColor: "divider",
            }}
          >
            <Typography variant="subtitle2" sx={{ flex: 1 }}>
              Project files
            </Typography>
            <GitFilesMenu
              api={api}
              appId={appId}
              companyId={companyId}
              getToken={getToken}
              openDialog={openDialog}
            />
            {app && app.uid === currentUid && app.appType === APP_TYPE_EXTERNAL && (
              <PublishMenu api={api} appId={appId} />
            )}
            <Tooltip title="New file">
              <IconButton size="small" onClick={() => setCreateFileOpen(true)}>
                <LucideIcon name="Plus" size={18} />
              </IconButton>
            </Tooltip>
          </Box>
          <Box sx={{ flex: 1, overflow: "auto", py: 1 }}>
            <FileTreeView
              nodes={fileTree}
              activeFile={activeFile}
              onPick={(path) => {
                onPickFile(path);
                setRightTab("code");
                setLeftDrawerOpen(false);
              }}
              onDelete={(path) => setConfirmDelete(path)}
            />
          </Box>
        </Box>
      )}

      {leftTab === "tables" && (
        <TablesPanel
          schemas={flexSchemas}
          appType={app?.appType}
          onAskAi={(prompt) => {
            setLeftTab("chat");
            setPrompt(prompt);
          }}
        />
      )}
    </>
  );

  return (
    <Box sx={{ height: "100vh", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 2,
          py: 1,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        {isMobile && (
          <IconButton
            size="small"
            onClick={() => setLeftDrawerOpen(true)}
            aria-label="open menu"
          >
            <LucideIcon name="Menu" size={22} />
          </IconButton>
        )}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 36,
            height: 36,
            borderRadius: 1,
            backgroundColor: "action.hover",
            flexShrink: 0,
          }}
        >
          <LucideIcon name={app.icon || "AppWindowMac"} size={22} />
        </Box>
        <Typography variant="h5" sx={{ flex: 1, fontWeight: 600 }}>
          {app.name}
        </Typography>
        <Chip
          size="small"
          label={`${fileEntries.length} files`}
          color="default"
        />
        <Chip
          size="small"
          label={`${flexSchemas.length} ${flexSchemas.length === 1 ? "table" : "tables"}`}
          color={flexSchemas.length ? "primary" : "default"}
          variant={flexSchemas.length ? "filled" : "outlined"}
        />
        <Tooltip
          title={
            menuAppIds.includes(appId)
              ? "Remove from my menu"
              : "Add to my menu"
          }
        >
          <IconButton
            size="small"
            onClick={() => {
              const next = menuAppIds.includes(appId)
                ? menuAppIds.filter((x) => x !== appId)
                : [...menuAppIds, appId];
              onMenuAppIdsChange(next);
            }}
          >
            {menuAppIds.includes(appId) ? (
              <Box sx={{ color: "primary.main", display: "flex" }}>
                <LucideIcon name="Bookmark" size={20} />
              </Box>
            ) : (
              <LucideIcon name="BookmarkPlus" size={20} />
            )}
          </IconButton>
        </Tooltip>
        <Tooltip title="Refresh preview">
          <IconButton size="small" onClick={() => setPreviewKey((k) => k + 1)}>
            <LucideIcon name="RefreshCw" size={20} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Close">
          <IconButton onClick={onClose} aria-label="close">
            <LucideIcon name="X" size={20} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Body */}
      <Box sx={{ flex: 1, display: "flex", overflow: "hidden" }}>
        {/* Left rail — tabbed Chat / Files / Tables */}
        {isMobile ? (
          <Drawer
            anchor="left"
            open={leftDrawerOpen}
            onClose={() => setLeftDrawerOpen(false)}
            sx={{ zIndex: (t) => t.zIndex.modal + 10 }}
            PaperProps={{
              sx: {
                width: "85%",
                maxWidth: 460,
                display: "flex",
                flexDirection: "column",
              },
            }}
          >
            {leftRailContent}
          </Drawer>
        ) : (
          <Box
            sx={{
              width: 460,
              minWidth: 360,
              display: "flex",
              flexDirection: "column",
              borderRight: 1,
              borderColor: "divider",
            }}
          >
            {leftRailContent}
          </Box>
        )}
        {/* Preview / code (right) */}
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
          <Box
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              display: "flex",
              alignItems: "center",
              px: 1,
            }}
          >
            <Tabs
              value={rightTab}
              onChange={(_, v) => setRightTab(v)}
              sx={{ minHeight: 40 }}
            >
              <Tab
                value="preview"
                label="Preview"
                icon={<LucideIcon name="Eye" size={16} />}
                iconPosition="start"
                sx={{ minHeight: 40 }}
              />
              <Tab
                value="code"
                label={activeFile ? `Edit ${activeFile}` : "Edit"}
                icon={<LucideIcon name="Code" size={16} />}
                iconPosition="start"
                sx={{ minHeight: 40 }}
                disabled={!activeFile}
              />
            </Tabs>
          </Box>
          <Box sx={{ flex: 1, overflow: "hidden" }}>
            {rightTab === "preview" ? (
              <Box
                sx={{
                  position: "relative",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  bgcolor: "action.hover",
                }}
              >
                {/* Viewport switcher */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    px: 1,
                    py: 0.5,
                    borderBottom: 1,
                    borderColor: "divider",
                    bgcolor: "background.paper",
                  }}
                >
                  {(
                    [
                      {
                        id: "desktop",
                        icon: <LucideIcon name="Monitor" size={18} />,
                        label: "Desktop",
                      },
                      {
                        id: "tablet",
                        icon: <LucideIcon name="Tablet" size={18} />,
                        label: "Tablet (768×1024)",
                      },
                      {
                        id: "mobile",
                        icon: <LucideIcon name="Smartphone" size={18} />,
                        label: "Mobile (390×844)",
                      },
                    ] as const
                  ).map((v) => (
                    <Tooltip key={v.id} title={v.label}>
                      <IconButton
                        size="small"
                        onClick={() => setPreviewViewport(v.id)}
                        sx={{
                          color:
                            previewViewport === v.id
                              ? "primary.main"
                              : "text.secondary",
                          bgcolor:
                            previewViewport === v.id
                              ? "action.selected"
                              : "transparent",
                        }}
                      >
                        {v.icon}
                      </IconButton>
                    </Tooltip>
                  ))}
                  <Box sx={{ flex: 1 }} />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ pr: 1 }}
                  >
                    {previewViewport === "desktop"
                      ? "Fluid"
                      : previewViewport === "tablet"
                      ? "768 × 1024"
                      : "390 × 844"}
                  </Typography>
                  {app?.appType === APP_TYPE_EXTERNAL && (
                    <Tooltip title="Theme">
                      <IconButton
                        size="small"
                        onClick={(e) => setThemeAnchor(e.currentTarget)}
                        sx={{
                          color: themeAnchor ? "primary.main" : "text.secondary",
                          bgcolor: themeAnchor ? "action.selected" : "transparent",
                        }}
                      >
                        <LucideIcon name="Palette" size={18} />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Runtime console">
                    <IconButton
                      size="small"
                      onClick={() => setConsoleOpen((v) => !v)}
                      sx={{
                        color: consoleOpen ? "primary.main" : "text.secondary",
                        bgcolor: consoleOpen ? "action.selected" : "transparent",
                      }}
                    >
                      <Badge
                        badgeContent={consoleEntries.filter((e) => e.kind !== "warn").length}
                        color="error"
                        max={99}
                      >
                        <LucideIcon name="Terminal" size={18} />
                      </Badge>
                    </IconButton>
                  </Tooltip>
                </Box>
                {app?.appType === APP_TYPE_EXTERNAL && (
                  <ThemePanel
                    open={Boolean(themeAnchor)}
                    anchorEl={themeAnchor}
                    onClose={() => setThemeAnchor(null)}
                    appId={appId}
                    api={api}
                    onSaved={async () => {
                      await loadFiles();
                      setPreviewKey((k) => k + 1);
                    }}
                  />
                )}
                {/* Sized iframe wrapper */}
                <Box
                  sx={{
                    flex: 1,
                    overflow: "auto",
                    display: "flex",
                    justifyContent: "center",
                    alignItems:
                      previewViewport === "desktop" ? "stretch" : "flex-start",
                    p: previewViewport === "desktop" ? 0 : 2,
                  }}
                >
                  <Box
                    sx={{
                      width:
                        previewViewport === "desktop"
                          ? "100%"
                          : previewViewport === "tablet"
                          ? 768
                          : 390,
                      height:
                        previewViewport === "desktop"
                          ? "100%"
                          : previewViewport === "tablet"
                          ? 1024
                          : 844,
                      maxWidth: "100%",
                      flexShrink: 0,
                      bgcolor: "background.paper",
                      boxShadow:
                        previewViewport === "desktop" ? 0 : 3,
                      borderRadius: previewViewport === "desktop" ? 0 : 1,
                      overflow: "hidden",
                    }}
                  >
                    <AppRuntime
                      files={files}
                      getToken={getToken}
                      companyId={companyId}
                      appId={appId}
                      hostname={hostname}
                      mode={app?.appType === APP_TYPE_EXTERNAL ? "external" : "platform"}
                      reloadKey={previewKey}
                      onRuntimeEvent={onRuntimeEvent}
                    />
                  </Box>
                </Box>
                <RuntimeConsolePanel
                  open={consoleOpen}
                  onClose={() => setConsoleOpen(false)}
                  entries={consoleEntries}
                  onClear={() => setConsoleEntries([])}
                  onSendToChat={sendErrorToChat}
                  appId={appId}
                />
              </Box>
            ) : activeFile ? (
              <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                <Box
                  sx={{
                    p: 1,
                    display: "flex",
                    gap: 1,
                    borderBottom: 1,
                    borderColor: "divider",
                  }}
                >
                  {editorError && (
                    <Alert severity="error" sx={{ flex: 1 }}>
                      {editorError}
                    </Alert>
                  )}
                  <Box sx={{ flex: 1 }} />
                  <LoadingButton
                    variant="contained"
                    size="small"
                    onClick={saveFile}
                    loading={savingFile}
                    disabled={!editorDirty}
                  >
                    Save
                  </LoadingButton>
                </Box>
                <Box sx={{ flex: 1, minHeight: 0 }}>
                  <CodeEditor
                    path={activeFile}
                    value={editorContent}
                    onChange={(next) => {
                      setEditorContent(next);
                      setEditorDirty(true);
                    }}
                    onSave={() => saveFile()}
                  />
                </Box>
              </Box>
            ) : null}
          </Box>
        </Box>
      </Box>

      {/* Create file dialog */}
      <Dialog
        open={createFileOpen}
        onClose={() => setCreateFileOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Create file</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Path"
            placeholder="/pages/Leads.tsx"
            value={createFilePath}
            onChange={(e) => setCreateFilePath(e.target.value)}
            sx={{ mt: 1 }}
            helperText="Examples: /pages/Leads.tsx, /components/LeadCard.tsx, /lib/format.ts. Defaults to .tsx if you omit an extension."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateFileOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={onCreateFile} disabled={!createFilePath.trim()}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm delete */}
      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)} maxWidth="xs">
        <DialogTitle>Delete file</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Delete <strong>{confirmDelete}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => confirmDelete && deleteFile(confirmDelete)}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rename thread */}
      <Dialog open={!!renamingThread} onClose={() => setRenamingThread(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Rename thread</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            value={renameDraft}
            onChange={(e) => setRenameDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") submitRename();
            }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenamingThread(null)}>Cancel</Button>
          <Button variant="contained" onClick={submitRename} disabled={!renameDraft.trim()}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Sticky todo list shown above the chat input — mirrors the way Claude Code
// surfaces its TodoWrite progress while a task is running.
const TodoListPanel: React.FC<{ todos: TodoItem[]; streaming: boolean }> = ({
  todos,
  streaming,
}) => {
  const [collapsed, setCollapsed] = useState(false);

  const completed = todos.filter((t) => t.status === "completed").length;
  const total = todos.length;
  const allDone = total > 0 && completed === total;
  const hasActive = todos.some((t) => t.status === "in_progress");

  // Hide the panel once the agent finishes everything — same behavior as
  // Claude Code, which clears its todo display on completion. We keep it
  // visible while streaming so the final ✅ is briefly seen.
  if (!todos.length || (allDone && !streaming)) return null;

  return (
    <Box
      sx={{
        borderTop: 1,
        borderColor: "divider",
        bgcolor: "action.hover",
      }}
    >
      <Box
        onClick={() => setCollapsed((v) => !v)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 2,
          py: 1,
          cursor: "pointer",
          "&:hover": { bgcolor: "action.selected" },
        }}
      >
        {hasActive && streaming ? (
          <CircularProgress size={14} thickness={5} />
        ) : allDone ? (
          <Box sx={{ color: "primary.main", display: "flex" }}>
            <LucideIcon name="CheckCircle2" size={18} />
          </Box>
        ) : (
          <Box sx={{ color: "text.secondary", display: "flex" }}>
            <LucideIcon name="Circle" size={18} />
          </Box>
        )}
        <Typography variant="caption" sx={{ flex: 1, fontWeight: 600 }}>
          Todos · {completed}/{total}
        </Typography>
        {collapsed ? (
          <LucideIcon name="ChevronRight" size={18} />
        ) : (
          <LucideIcon name="ChevronDown" size={18} />
        )}
      </Box>
      {!collapsed && (
        <Box sx={{ px: 2, pb: 1.25 }}>
          {todos.map((t, i) => (
            <TodoRow key={i} todo={t} streaming={streaming} />
          ))}
        </Box>
      )}
    </Box>
  );
};

const TodoRow: React.FC<{ todo: TodoItem; streaming: boolean }> = ({
  todo,
  streaming,
}) => {
  const isDone = todo.status === "completed";
  const isActive = todo.status === "in_progress";
  const label = isActive && todo.activeForm ? todo.activeForm : todo.content;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1,
        py: 0.5,
        fontSize: 13,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", height: 20, mt: "1px" }}>
        {isDone ? (
          <Box sx={{ color: "primary.main", display: "flex" }}>
            <LucideIcon name="CheckCircle2" size={16} />
          </Box>
        ) : isActive && streaming ? (
          <CircularProgress size={14} thickness={5} />
        ) : (
          <Box sx={{ color: isActive ? "primary.main" : "text.secondary", display: "flex" }}>
            <LucideIcon name="Circle" size={16} />
          </Box>
        )}
      </Box>
      <Typography
        variant="body2"
        sx={{
          flex: 1,
          color: isDone ? "text.secondary" : "text.primary",
          textDecoration: isDone ? "line-through" : "none",
          fontWeight: isActive ? 600 : 400,
          lineHeight: 1.4,
        }}
      >
        {label}
      </Typography>
    </Box>
  );
};

const ChatMessageView: React.FC<{ message: ChatMsg }> = ({ message }) => (
  <Box
    sx={{
      mb: 2,
      p: 2,
      borderRadius: 2,
      bgcolor: message.role === "user" ? "action.hover" : "background.paper",
      border: 1,
      borderColor: "divider",
    }}
  >
    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
      {message.role === "user" ? "You" : "AI Assistant"}
    </Typography>
    {(message.toolCalls || [])
      .filter((t) => t.name !== "todo_write")
      .map((t, i) => (
        <ToolCallView key={i} tool={t} />
      ))}
    {message.content && (
      <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", mt: message.toolCalls?.length ? 1 : 0 }}>
        {message.content}
      </Typography>
    )}
  </Box>
);

const ToolCallView: React.FC<{ tool: BuilderToolEvent }> = ({ tool }) => {
  const [expanded, setExpanded] = useState(false);
  const display = TOOL_DISPLAY[tool.name] || tool.name;
  const summary = summarizeToolArgs(tool);

  return (
    <Box
      onClick={() => setExpanded((v) => !v)}
      sx={{
        my: 0.5,
        p: 1,
        borderRadius: 1,
        bgcolor: "action.hover",
        fontSize: 12,
        cursor: "pointer",
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        borderLeft: 3,
        borderColor: tool.ok === false ? "error.main" : tool.ok ? "primary.main" : "warning.main",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box sx={{ flex: 1 }}>
          <strong>{display}</strong>
          {summary ? ` · ${summary}` : ""}
        </Box>
        {tool.ok === false && (
          <Chip size="small" color="error" label="error" sx={{ height: 18, fontSize: 10 }} />
        )}
      </Box>
      {expanded && tool.result && (
        <Box
          component="pre"
          sx={{
            mt: 1,
            mb: 0,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            fontSize: 11,
            color: "text.secondary",
          }}
        >
          {tool.result.length > 800 ? tool.result.slice(0, 800) + "…" : tool.result}
        </Box>
      )}
    </Box>
  );
};

function summarizeToolArgs(tool: BuilderToolEvent): string {
  const args = tool.args || {};
  if (args.path) return String(args.path);
  if (args.file_path) return String(args.file_path);
  if (args.table) return String(args.table);
  if (args.name && tool.name === "create_table") return String(args.name);
  return "";
}

// ---------------------------------------------------------------------------
// File tree
// ---------------------------------------------------------------------------

interface TreeNode {
  name: string;
  path: string;
  isFolder: boolean;
  children?: TreeNode[];
  entry?: BuilderFileEntry;
}

function buildFileTree(entries: BuilderFileEntry[]): TreeNode[] {
  const root: TreeNode = { name: "", path: "/", isFolder: true, children: [] };
  for (const e of entries) {
    const parts = e.path.split("/").filter(Boolean);
    let cur = root;
    for (let i = 0; i < parts.length; i++) {
      const isLast = i === parts.length - 1;
      const name = parts[i];
      const fullPath =
        "/" + parts.slice(0, i + 1).join("/");
      let next = (cur.children || []).find((c) => c.name === name);
      if (!next) {
        next = {
          name,
          path: fullPath,
          isFolder: !isLast,
          children: isLast ? undefined : [],
          entry: isLast ? e : undefined,
        };
        cur.children = [...(cur.children || []), next];
      }
      cur = next;
    }
  }
  // Sort: folders first, then files; alpha within each.
  const sortRec = (node: TreeNode) => {
    if (node.children) {
      node.children.sort((a, b) => {
        if (a.isFolder !== b.isFolder) return a.isFolder ? -1 : 1;
        return a.name.localeCompare(b.name);
      });
      node.children.forEach(sortRec);
    }
  };
  sortRec(root);
  return root.children || [];
}

function fileIconFor(name: string) {
  return <LucideIcon name="File" size={14} />;
}

const FileTreeView: React.FC<{
  nodes: TreeNode[];
  activeFile: string | null;
  onPick: (path: string) => void;
  onDelete: (path: string) => void;
}> = ({ nodes, activeFile, onPick, onDelete }) => {
  return (
    <Box sx={{ pr: 1 }}>
      {nodes.map((n) => (
        <FileTreeRow
          key={n.path}
          node={n}
          depth={0}
          activeFile={activeFile}
          onPick={onPick}
          onDelete={onDelete}
          startExpanded
        />
      ))}
    </Box>
  );
};

const FileTreeRow: React.FC<{
  node: TreeNode;
  depth: number;
  activeFile: string | null;
  onPick: (path: string) => void;
  onDelete: (path: string) => void;
  startExpanded?: boolean;
}> = ({ node, depth, activeFile, onPick, onDelete, startExpanded }) => {
  const [expanded, setExpanded] = useState(!!startExpanded);
  const indent = 1 + depth * 1.5;
  const isActive = !node.isFolder && activeFile === node.path;

  if (node.isFolder) {
    return (
      <>
        <Box
          onClick={() => setExpanded((v) => !v)}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            pl: indent,
            pr: 1,
            py: 0.4,
            cursor: "pointer",
            color: "text.secondary",
            "&:hover": { bgcolor: "action.hover" },
          }}
        >
          {expanded ? (
            <LucideIcon name="ChevronDown" size={16} />
          ) : (
            <LucideIcon name="ChevronRight" size={16} />
          )}
          {expanded ? (
            <Box sx={{ color: "warning.main", display: "flex" }}>
              <LucideIcon name="FolderOpen" size={16} />
            </Box>
          ) : (
            <Box sx={{ color: "warning.main", display: "flex" }}>
              <LucideIcon name="Folder" size={16} />
            </Box>
          )}
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {node.name}
          </Typography>
        </Box>
        {expanded &&
          (node.children || []).map((child) => (
            <FileTreeRow
              key={child.path}
              node={child}
              depth={depth + 1}
              activeFile={activeFile}
              onPick={onPick}
              onDelete={onDelete}
              startExpanded
            />
          ))}
      </>
    );
  }

  return (
    <Box
      onClick={() => onPick(node.path)}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        pl: indent + 2.25,
        pr: 1,
        py: 0.4,
        cursor: "pointer",
        bgcolor: isActive ? "action.selected" : "transparent",
        "&:hover": { bgcolor: isActive ? "action.selected" : "action.hover" },
        "&:hover .row-actions": { visibility: "visible" },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", color: "text.secondary", fontSize: 14 }}>
        {fileIconFor(node.name)}
      </Box>
      <Typography
        variant="body2"
        sx={{
          flex: 1,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {node.name}
      </Typography>
      <Box className="row-actions" sx={{ visibility: "hidden" }}>
        <Tooltip title="Delete">
          <IconButton
            size="small"
            onClick={(ev) => {
              ev.stopPropagation();
              onDelete(node.path);
            }}
          >
            <LucideIcon name="Trash2" size={14} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Tables panel
// ---------------------------------------------------------------------------

// Visual representation of a flex table's scope. External apps have three
// scopes (global / user / org); internal apps have two (company / user).
// 'company' on an external app is a legacy value the backend treats as
// 'global' — render it the same way so the UI doesn't confuse the user.
const scopeChip = (
  scope: string | undefined,
  appType: string | undefined,
): { label: string; tooltip: string; filled: boolean } => {
  const isExternal = appType === APP_TYPE_EXTERNAL;
  const effective =
    isExternal && (scope === "company" || !scope) ? "global" : scope || "company";
  if (effective === "user") {
    return {
      label: "per-user",
      tooltip: isExternal
        ? "Per-user: each signed-in end-user only sees rows they created"
        : "Per-user: each platform member only sees rows they created",
      filled: true,
    };
  }
  if (effective === "org") {
    return {
      label: "per-org",
      tooltip:
        "Per-org: members of the same end-user organization share these rows",
      filled: true,
    };
  }
  if (effective === "global") {
    return {
      label: "shared",
      tooltip: "Shared: every end-user of this app sees the same rows",
      filled: false,
    };
  }
  return {
    label: "shared",
    tooltip: "Shared: every member of the platform tenant sees the same rows",
    filled: false,
  };
};

const TablesPanel: React.FC<{
  schemas: FlexSchema[];
  appType?: string;
  onAskAi: (prompt: string) => void;
}> = ({ schemas, appType, onAskAi }) => {
  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <Box
        sx={{
          p: 1,
          pl: 2,
          display: "flex",
          alignItems: "center",
          gap: 1,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Typography variant="subtitle2" sx={{ flex: 1 }}>
          Workspace tables
        </Typography>
        <Tooltip title="Ask AI to create a table">
          <IconButton
            size="small"
            onClick={() =>
              onAskAi("Create a new table for me. I'd like to track ")
            }
          >
            <LucideIcon name="Plus" size={18} />
          </IconButton>
        </Tooltip>
      </Box>
      <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
        {schemas.length === 0 ? (
          <Box textAlign="center" py={4}>
            <Box sx={{ color: "text.disabled", mb: 1, display: "flex", justifyContent: "center" }}>
              <LucideIcon name="Table" size={40} />
            </Box>
            <Typography color="text.secondary" gutterBottom>
              No tables yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Ask the AI to set up the data your app needs.
            </Typography>
            <Stack spacing={1}>
              <Chip
                label="Create a tasks table"
                onClick={() =>
                  onAskAi(
                    "Create a `tasks` table with columns: title (text, required), status (text), due_date (date), notes (longtext).",
                  )
                }
                sx={{ cursor: "pointer" }}
              />
              <Chip
                label="Create a leads table"
                onClick={() =>
                  onAskAi(
                    "Create a `leads` table with columns: name (text), email (email, required), company (text), score (number), notes (longtext).",
                  )
                }
                sx={{ cursor: "pointer" }}
              />
            </Stack>
          </Box>
        ) : (
          <Stack spacing={2}>
            {schemas.map((s) => (
              <Box
                key={s.id}
                sx={{
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    p: 1.25,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    bgcolor: "action.hover",
                  }}
                >
                  <Box sx={{ color: "primary.main", display: "flex" }}>
                    <LucideIcon name="Database" size={18} />
                  </Box>
                  <Typography variant="subtitle2" sx={{ flex: 1 }}>
                    {s.name}
                  </Typography>
                  {(() => {
                    const chip = scopeChip(s.scope, appType);
                    return (
                      <Tooltip title={chip.tooltip}>
                        <Chip
                          size="small"
                          label={chip.label}
                          color={chip.filled ? "primary" : "default"}
                          variant={chip.filled ? "filled" : "outlined"}
                        />
                      </Tooltip>
                    );
                  })()}
                  <Chip size="small" label={`${s.columns?.length || 0} cols`} />
                </Box>
                {s.description && (
                  <Box sx={{ px: 1.5, pt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      {s.description}
                    </Typography>
                  </Box>
                )}
                {s.columns && s.columns.length > 0 ? (
                  <Box>
                    {s.columns.map((c) => (
                      <Box
                        key={c.id}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          px: 1.5,
                          py: 0.6,
                          borderTop: 1,
                          borderColor: "divider",
                          fontFamily:
                            "ui-monospace, SFMono-Regular, Menlo, monospace",
                          fontSize: 12,
                        }}
                      >
                        <Box sx={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis" }}>
                          {c.name}
                        </Box>
                        <Chip
                          size="small"
                          label={c.type}
                          sx={{ height: 18, fontSize: 10 }}
                        />
                        {c.required && (
                          <Chip
                            size="small"
                            label="required"
                            color="primary"
                            sx={{ height: 18, fontSize: 10 }}
                          />
                        )}
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ p: 1.5, display: "block" }}
                  >
                    no columns
                  </Typography>
                )}
                <Box
                  sx={{
                    px: 1.5,
                    py: 1,
                    borderTop: 1,
                    borderColor: "divider",
                    display: "flex",
                    gap: 1,
                  }}
                >
                  <Button
                    size="small"
                    onClick={() =>
                      onAskAi(`Add a new column to the "${s.name}" table. Column: `)
                    }
                  >
                    Add column
                  </Button>
                </Box>
              </Box>
            ))}
          </Stack>
        )}
      </Box>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Thread switcher
// ---------------------------------------------------------------------------

const ThreadSwitcher: React.FC<{
  threads: BuilderThreadSummary[];
  activeThreadId: string | null;
  onPick: (id: string) => void;
  onNew: () => void;
  onRename: (t: BuilderThreadSummary) => void;
  onDelete: (id: string) => void;
}> = ({ threads, activeThreadId, onPick, onNew, onRename, onDelete }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const active = threads.find((t) => t.id === activeThreadId);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        px: 1,
        py: 0.5,
        borderBottom: 1,
        borderColor: "divider",
      }}
    >
      <Tooltip title="Switch thread">
        <Button
          size="small"
          variant="text"
          onClick={(e) => setAnchor(e.currentTarget)}
          endIcon={<LucideIcon name="ChevronsUpDown" size={20} />}
          sx={{
            flex: 1,
            justifyContent: "flex-start",
            textTransform: "none",
            color: "text.primary",
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              textAlign: "left",
            }}
          >
            <Typography variant="body2" fontWeight={500} noWrap>
              {active?.title || "Thread"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {active ? `${active.messageCount} message${active.messageCount === 1 ? "" : "s"}` : ""}
            </Typography>
          </Box>
        </Button>
      </Tooltip>
      <Tooltip title="New thread">
        <IconButton size="small" onClick={onNew}>
          <LucideIcon name="FilePlus" size={18} />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={!!anchor}
        onClose={() => setAnchor(null)}
        slotProps={{ paper: { sx: { minWidth: 280, maxWidth: 360 } } }}
      >
        {threads.map((t) => {
          const isActive = t.id === activeThreadId;
          return (
            <MenuItem
              key={t.id}
              selected={isActive}
              onClick={() => {
                setAnchor(null);
                if (!isActive) onPick(t.id);
              }}
              sx={{ alignItems: "flex-start", gap: 1 }}
            >
              <ListItemIcon sx={{ minWidth: 28, mt: 0.5 }}>
                {isActive ? <LucideIcon name="Check" size={18} /> : <LucideIcon name="MessageCircle" size={18} />}
              </ListItemIcon>
              <ListItemText
                primary={t.title || "Untitled"}
                secondary={`${t.messageCount} message${t.messageCount === 1 ? "" : "s"}`}
                primaryTypographyProps={{ noWrap: true }}
              />
              <Box sx={{ display: "flex", gap: 0.25, ml: 1 }}>
                <Tooltip title="Rename">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setAnchor(null);
                      onRename(t);
                    }}
                  >
                    <LucideIcon name="Pencil" size={18} />
                  </IconButton>
                </Tooltip>
                <Tooltip title={threads.length <= 1 ? "Can't delete the last thread" : "Delete thread"}>
                  <span>
                    <IconButton
                      size="small"
                      disabled={threads.length <= 1}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (threads.length <= 1) return;
                        setAnchor(null);
                        onDelete(t.id);
                      }}
                    >
                      <LucideIcon name="Trash2" size={18} />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </MenuItem>
          );
        })}
        <Divider />
        <MenuItem
          onClick={() => {
            setAnchor(null);
            onNew();
          }}
        >
          <ListItemIcon sx={{ minWidth: 28 }}>
            <LucideIcon name="FilePlus" size={18} />
          </ListItemIcon>
          <ListItemText primary="New thread" />
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default BuilderEditor;
